import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { useTranslator, useToast, callNativeMobileApp } from '@front-activerse/common-components';
import type { UnlinkActivityResult } from '@front-activerse/common-components';
import TrackerItemMenu from '../TrackerItemMenu';

interface IMergedActivityMenuProps {
  activityId: number;
  onUnlink: (activityId: number) => Promise<UnlinkActivityResult>;
  onRemove: (activityId: number) => Promise<any>;
  date: string;
}

type DialogMode = 'none' | 'unlink' | 'remove';

const MergedActivityMenu: React.FC<IMergedActivityMenuProps> = ({
  activityId,
  onUnlink,
  onRemove,
  date,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const [dialog, setDialog] = useState<DialogMode>('none');
  const [pending, setPending] = useState(false);
  const [unlinkRetryCount, setUnlinkRetryCount] = useState(0);

  const closeDialog = () => setDialog('none');

  // Reactstrap's Modal exposes `labelledBy` but not `describedBy`. After the
  // dialog mounts we attach aria-describedby to the role="dialog" element.
  useEffect(() => {
    if (dialog === 'none') return;
    let cancelled = false;
    const labelId = dialog === 'unlink' ? 'unlink-dialog-title' : 'merged-remove-dialog-title';
    const describedId = dialog === 'unlink' ? 'unlink-dialog-body' : 'merged-remove-dialog-body';
    const attach = (attempt = 0) => {
      if (cancelled) return;
      const node = document.querySelector<HTMLDivElement>(
        `[role="dialog"][aria-labelledby="${labelId}"]`
      );
      if (node) {
        node.setAttribute('aria-describedby', describedId);
        return;
      }
      if (attempt < 10) {
        setTimeout(() => attach(attempt + 1), 10);
      }
    };
    attach();
    return () => {
      cancelled = true;
    };
  }, [dialog]);

  const showUnlinkErrorToast = (withRetry: boolean) => {
    toastError(
      <div className="d-flex align-items-center justify-content-between">
        <span>{t('tracker.mergedActivity.unlinkError')}</span>
        {withRetry && (
          <Button
            color="link"
            className="p-0 ms-2 text-white text-decoration-underline"
            onClick={() => setDialog('unlink')}
            data-testid="merged-activity-unlink-retry-button"
          >
            {t('tracker.mergedActivity.unlinkRetry')}
          </Button>
        )}
      </div>
    );
  };

  const notifyNativeRefresh = () => {
    callNativeMobileApp({
      action: 'refresh_data',
      payload: { module: 'tracker', date },
    });
  };

  const handleUnlinkConfirm = async () => {
    setPending(true);
    setDialog('none');
    try {
      const result = await onUnlink(activityId);
      if (result.ok) {
        setUnlinkRetryCount(0);
        notifyNativeRefresh();
      } else {
        const next = unlinkRetryCount + 1;
        setUnlinkRetryCount(next);
        showUnlinkErrorToast(next < 2);
      }
    } catch {
      const next = unlinkRetryCount + 1;
      setUnlinkRetryCount(next);
      showUnlinkErrorToast(next < 2);
    } finally {
      setPending(false);
    }
  };

  const handleRemoveConfirm = async () => {
    setPending(true);
    setDialog('none');
    try {
      const response = await onRemove(activityId);
      if (response && response.code === 200) {
        notifyNativeRefresh();
      } else {
        toastError();
      }
    } catch {
      toastError();
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <TrackerItemMenu
        ariaLabel={t('tracker.mergedActivity.moreActionsLabel')}
        testId="merged-activity-kebab-button"
        items={[
          {
            key: 'unlink',
            label: t('tracker.mergedActivity.unlink'),
            onSelect: () => setDialog('unlink'),
            testId: 'merged-activity-unlink-menuitem',
          },
          {
            key: 'remove',
            label: t('tracker.history.removePopup.removeButton'),
            onSelect: () => setDialog('remove'),
            destructive: true,
            testId: 'merged-activity-remove-menuitem',
          },
        ]}
      />

      <Modal
        isOpen={dialog === 'unlink'}
        toggle={closeDialog}
        centered
        size="md"
        labelledBy="unlink-dialog-title"
      >
        <ModalBody>
          <h4 id="unlink-dialog-title" className="title-medium semi-bold mb-l mt-s">
            {t('tracker.mergedActivity.confirmDialog.title')}
          </h4>
          <p id="unlink-dialog-body" className="mb-0 text-text-variant-color">
            {t('tracker.mergedActivity.confirmDialog.body')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button
                color="secondary"
                outline
                className="w-100"
                onClick={closeDialog}
                data-testid="merged-activity-unlink-cancel-button"
              >
                {t('tracker.mergedActivity.confirmDialog.cancel')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={handleUnlinkConfirm}
                disabled={pending}
                data-testid="merged-activity-unlink-confirm-button"
              >
                {t('tracker.mergedActivity.confirmDialog.confirm')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={dialog === 'remove'}
        toggle={closeDialog}
        centered
        size="md"
        labelledBy="merged-remove-dialog-title"
      >
        <ModalBody>
          <h4 id="merged-remove-dialog-title" className="title-medium semi-bold mb-l mt-s">
            {t('tracker.history.removePopup.syncedActivity.title')}
          </h4>
          <p id="merged-remove-dialog-body" className="mb-0 text-text-variant-color">
            {t('tracker.history.removePopup.syncedActivity.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button
                color="secondary"
                outline
                className="w-100"
                onClick={closeDialog}
                data-testid="merged-activity-remove-cancel-button"
              >
                {t('tracker.history.removePopup.cancelButton')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={handleRemoveConfirm}
                disabled={pending}
                data-testid="merged-activity-remove-confirm-button"
              >
                {t('tracker.history.removePopup.removeButton')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MergedActivityMenu;
